/**
 * @flow
 */

import React from 'react';
import styled from 'styled-components';
import { List, Loader } from 'semantic-ui-react';
import { isEmpty, get } from 'lodash';
import { FormattedMessage } from 'react-intl';
import InfiniteScroll from 'react-infinite-scroller';
import { format, addMinutes, startOfDay } from 'date-fns';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';

import { Table, EllipsisLoader } from '../../../components';
import SeminarDetailsButton from './SeminarDetailsButton';
import EditSeminarButton from './EditSeminarButton';
import DeleteSeminarButton from './DeleteSeminarButton';
import { GET_SEMINARS_QUERY } from '../containers/queries';
import type { Seminar, SeminarEdge, PageInfo } from '../../../type';

export { GET_SEMINARS_QUERY };

type SeminarsListViewProps = {
    seminars: Array<SeminarEdge>,
    loadMore: Function,
    pageInfo: PageInfo,
};

type SeminarRowProps = {
    seminar: Seminar,
};

const LoadMoreSpinner = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={6} textAlign="center">
            <EllipsisLoader size="1em" />
        </Table.Cell>
    </Table.Row>
);
const NameCell = styled(Table.Cell)`
    padding: 0px !important;
`;

const EmptyMessage = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={6} textAlign="center">
            <FormattedMessage
                id="pages.seminars.SeminarList.noSeminar"
                defaultMessage={"You don't have seminars yet"}
            />
        </Table.Cell>
    </Table.Row>
);

const SeminarType = ({ type }) => {
    let typeName;

    if (type === 'HAIRDRESSER') {
        typeName = (
            <FormattedMessage
                id="pages.seminars.SeminarList.type.hairdresser"
                defaultMessage="Hairdresser"
            />
        );
    } else if (type === 'MANICURE') {
        typeName = (
            <FormattedMessage
                id="pages.seminars.SeminarList.type.manicyre"
                defaultMessage="Manicure"
            />
        );
    } else if (type === 'COSMETOLOGY') {
        typeName = (
            <FormattedMessage
                id="pages.seminars.SeminarList.type.cosmetology"
                defaultMessage="Cosmetology`"
            />
        );
    } else {
        typeName = (
            <FormattedMessage
                id="pages.seminars.SeminarList.type.all"
                defaultMessage="All"
            />
        );
    }

    return <Table.Cell>{typeName}</Table.Cell>;
};

const SeminarRow = ({ seminar }: SeminarRowProps) => (
    <Table.Row>
        <NameCell>
            <SeminarDetailsButton seminar={seminar}>
                {seminar.name}
            </SeminarDetailsButton>
        </NameCell>
        <Table.Cell>
            {format(new Date(seminar.startAt), 'dd.MM.yyyy HH:mm')}
        </Table.Cell>
        <Table.Cell>
            {format(addMinutes(startOfDay(new Date()), seminar.duration), 'HH:mm')}
        </Table.Cell>
        <SeminarType type={seminar.type} />
        <Table.Cell textAlign="right">
            {seminar.placeCount - seminar.visitors.count} / {seminar.placeCount}
        </Table.Cell>
        <Table.Cell>
            <List horizontal divided>
                <List.Item>
                    <EditSeminarButton seminar={seminar} />
                </List.Item>
                <List.Item>
                    <DeleteSeminarButton seminar={seminar} />
                </List.Item>
            </List>
        </Table.Cell>
    </Table.Row>
);

const SeminarsListView = ({ seminars, pageInfo, loadMore }: SeminarsListViewProps) => (
    <Table celled selectable striped fixed>
        <Table.Header>
            <Table.Row>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.seminars.SeminarList.name"
                        defaultMessage="Name"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.seminars.SeminarList.date"
                        defaultMessage="Date"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.seminars.SeminarList.duration"
                        defaultMessage="Duration"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.seminars.SeminarList.type"
                        defaultMessage="Type"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="right">
                    <FormattedMessage
                        id="pages.seminars.SeminarList.placeCount"
                        defaultMessage="Place count (available / total)"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.seminars.SeminarList.actions"
                        defaultMessage="Actions"
                    />
                </Table.HeaderCell>
            </Table.Row>
        </Table.Header>
        <InfiniteScroll
            element="tbody"
            pageStart={0}
            loadMore={loadMore}
            initialLoad={false}
            hasMore={pageInfo.hasNextPage}
            loader={<LoadMoreSpinner key={0} />}
            threshold={1}
            useWindow
        >
            {isEmpty(seminars) ? (
                <EmptyMessage />
            ) : (
                seminars.map(seminar => (
                    <SeminarRow key={seminar.cursor} seminar={seminar.node} />
                ))
            )}
        </InfiniteScroll>
    </Table>
);

function SeminarsList() {
    const searchQuery = useSelector(({ seminars }) => seminars.get('searchQuery'));

    const { loading, data, fetchMore } = useQuery(GET_SEMINARS_QUERY, {
        variables: { search: searchQuery || undefined, first: 20 },
        fetchPolicy: 'cache-and-network',
    });

    if (loading) return <Loader size="big" inline="centered" active />;

    const seminars = get(data, 'viewer.seminars.edges', []);
    const pageInfo = get(data, 'viewer.seminars.pageInfo', {});

    const loadMore = () => {
        if (loading || !pageInfo.hasNextPage) return false;
        fetchMore({
            query: GET_SEMINARS_QUERY,
            variables: {
                first: 20,
                after: pageInfo.endCursor,
                search: searchQuery || undefined,
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                const prevEdges = previousResult.viewer.seminars.edges;
                const newEdges = fetchMoreResult.viewer.seminars.edges;
                const newResults = Object.assign({}, fetchMoreResult);
                newResults.viewer.seminars.edges = [...prevEdges, ...newEdges];
                return newResults;
            },
        });
    };

    return <SeminarsListView seminars={seminars} pageInfo={pageInfo} loadMore={loadMore} />;
}

export default SeminarsList;
